import { Children, cloneElement, isValidElement } from 'react';
import type { CSSProperties, ReactElement, ReactNode } from 'react';

import { HelpLabel } from './InfoTip';

/* A dense table with sticky headers, row hover and its own horizontal scroll.
 *
 * The scroll container is the load-bearing part. Without it a twelve-column catalog widens the page
 * body, and the sidebar slides out of view the moment an analyst looks at the last column — so the
 * navigation disappears exactly when someone is deepest into a screen. Wide content scrolls inside
 * its own box instead.
 *
 * Columns are declared rather than inferred from the children. That is what lets the header, the
 * numeric alignment and the `scope="col"` come from one place: a caller writing its own `<thead>`
 * eventually forgets `scope`, and a header cell without it is a header only visually.
 *
 * **And it is what makes the mobile stacking possible at all.** Below 720px a table becomes a list
 * of cards, each cell on its own line labelled with its column — which needs every `<td>` to know
 * its heading. Because the columns are declared here, this component can attach them by position
 * rather than every caller remembering a `data-label` on every cell. Fifteen tables get the
 * behaviour without fifteen edits, and a new table cannot forget it.
 */

export interface Column {
  readonly key: string;
  readonly label: string;
  /** Right-aligns and prevents wrapping. For counts and versions, never for names. */
  readonly numeric?: boolean;
  /** Right-aligns without the numeric font. For a cell holding buttons. */
  readonly actions?: boolean;
  /** Header words laid out as equal tracks, so a cell can put one value under each.
   *
   * **`label` stays the single source for the stacked view**, and that is why this is additive
   * rather than a widening of `label` to a node: the mobile card renders the heading through
   * `attr(data-label)`, which can only read text, so a column whose header became markup would
   * lose its label on every phone. `parts` is what a wide table shows; `label` is what the column
   * *is*, and it must still read as one phrase.
   *
   * The cell opts in with `lq-table__parts` to inherit the same track count.
   */
  readonly parts?: readonly string[];
  /** What the column means, behind an "i" in its header. Not shown in the stacked phone view, where
   * the heading is `attr(data-label)` text and cannot hold a control. */
  readonly help?: string | undefined;
}

export interface DataTableProps {
  readonly columns: readonly Column[];
  readonly caption: string;
  readonly children: ReactNode;
  /** The design system's framed, square, heavy-headed table.
   *
   * **A variant rather than a change to the base**, because the base styles every table in the
   * product and this is opt-in: the reference design's treatment — an 11px 800-weight uppercase
   * header over a 2px rule, 44px rows, a 1px frame and no radius anywhere — is right for a ledger a
   * reader scans and wrong for the small two-column facts tables elsewhere. Opting in per screen is
   * what lets the two coexist without a flag day.
   */
  readonly variant?: 'ledger';
}

export function DataTable({
  columns,
  caption,
  children,
  variant,
}: DataTableProps): ReactElement {
  return (
    <div
      className={
        variant === 'ledger'
          ? 'lq-table-scroll lq-table-scroll--framed'
          : 'lq-table-scroll'
      }
    >
      <table className={variant === 'ledger' ? 'lq-table lq-table--ledger' : 'lq-table'}>
        {/* Visually hidden by convention in most kits; kept visible-to-AT only via the element
            itself, which is what `<caption>` already is. A table with no caption is one a screen
            reader announces as "table" with no indication of which table. */}
        <caption className="lq-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={
                  column.numeric
                    ? 'lq-table__num'
                    : column.actions
                      ? 'lq-table__actions'
                      : undefined
                }
              >
                {column.parts === undefined ? (
                  column.help === undefined ? (
                    column.label
                  ) : (
                    <HelpLabel subject={column.label} help={column.help}>
                      <span>{column.label}</span>
                    </HelpLabel>
                  )
                ) : (
                  <span
                    className="lq-table__parts"
                    style={{ '--lq-parts': column.parts.length } as CSSProperties}
                  >
                    {column.parts.map((part) => (
                      <span key={part}>{part}</span>
                    ))}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{labelled(children, columns)}</tbody>
      </table>
    </div>
  );
}

/* Attach each cell's column heading, for the stacked view.
 *
 * **Positional, and defensive about it.** A row whose cell count does not match the column count is
 * passed through untouched rather than mislabelled — a wrong label is worse than none, and a caller
 * with a `colSpan` or a conditional cell is doing something this cannot reason about. Anything that
 * is not a `<tr>` element (a fragment, a string, `null` from a conditional) is passed through too.
 *
 * `data-label` rather than a class: the value is the heading text, and CSS reads it with
 * `attr(data-label)`. Invisible on desktop, so it costs a wide table nothing.
 */
function labelled(children: ReactNode, columns: readonly Column[]): ReactNode {
  return Children.map(children, (row) => {
    if (!isValidElement<{ children?: ReactNode }>(row) || row.type !== 'tr') return row;
    const cells = Children.toArray(row.props.children);
    if (cells.length !== columns.length) return row;
    return cloneElement(row, {
      children: cells.map((cell, index) =>
        isValidElement<{ 'data-label'?: string }>(cell) && cell.type === 'td'
          ? cloneElement(cell, { 'data-label': columns[index]?.label ?? '' })
          : cell,
      ),
    });
  });
}

/** A cell holding a name with a subtitle beneath it.
 *
 * Two lines rather than one wrapped line: the subtitle is metadata *about* the name — a host and
 * port, a rule kind — and inline it reads as part of the name itself.
 */
export function PrimaryCell({
  children,
  secondary,
}: {
  readonly children: ReactNode;
  /* `| undefined` on every optional prop. `exactOptionalPropertyTypes` is on, so a caller
   * forwarding a conditional — `count={ready ? label : undefined}` — cannot pass explicit
   * undefined to a bare `prop?: T`, and every screen does exactly that. */
  readonly secondary?: string | undefined;
}): ReactElement {
  return (
    <>
      <div>{children}</div>
      {secondary !== undefined && <div className="lq-cell__secondary">{secondary}</div>}
    </>
  );
}
